import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Autocomplete,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    LinearProgress,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    EditNote as EditIcon,
    Undo as UndoIcon,
} from '@mui/icons-material';
import { getProject, getDescriptionReview, saveDescriptionReviewDecision } from '../services/api';

const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending', color: 'warning' },
    { value: 'accepted', label: 'Accepted', color: 'success' },
    { value: 'edited', label: 'Edited', color: 'info' },
    { value: 'rejected', label: 'Rejected', color: 'error' },
    { value: 'all', label: 'All', color: 'default' },
];

function DescriptionReviewPage() {
    const { projectId, sourceSystem, fileId } = useParams();
    const navigate = useNavigate();

    const [project, setProject] = useState(null);
    const [reviewData, setReviewData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [tableFilter, setTableFilter] = useState('all');
    const [columnFilter, setColumnFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [selected, setSelected] = useState(null);
    const [draftDescription, setDraftDescription] = useState('');

    useEffect(() => {
        const loadProject = async () => {
            try {
                const data = await getProject(projectId);
                setProject(data);
            } catch (err) {
                console.error('Error loading project:', err);
            }
        };
        loadProject();
    }, [projectId]);

    const fetchReviewData = async () => {
        try {
            setLoading(true);
            const data = await getDescriptionReview(projectId, sourceSystem, fileId, {
                page: page + 1,
                pageSize,
                statusFilter,
                tableFilter,
                columnFilter,
            });
            setReviewData(data);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load review data.');
            console.error('Error loading review data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviewData();
    }, [projectId, sourceSystem, fileId, statusFilter, tableFilter, columnFilter, page, pageSize]);

    useEffect(() => {
        if (!reviewData) {
            return;
        }
        if (!reviewData.columns || reviewData.columns.length === 0) {
            setSelected(null);
            return;
        }
        if (!selected) {
            setSelected(reviewData.columns[0]);
            return;
        }
        const match = reviewData.columns.find((col) => col.column_key === selected.column_key);
        if (match) {
            setSelected(match);
        }
    }, [reviewData]);

    useEffect(() => {
        if (!selected) {
            setDraftDescription('');
            return;
        }
        setDraftDescription(selected.definition || '');
    }, [selected]);

    const isDirty = selected ? draftDescription.trim() !== (selected.definition || '').trim() : false;
    const canSaveEdits = !!selected && isDirty && draftDescription.trim().length > 0 && !saving;

    const handleDecision = async (status) => {
        if (!selected) {
            return;
        }
        if (status === 'edited' && !draftDescription.trim()) {
            setError('Please provide a description before saving edits.');
            return;
        }
        if (status === 'rejected' && !window.confirm('Reject this description? It will be cleared.')) {
            return;
        }
        try {
            setSaving(true);
            await saveDescriptionReviewDecision({
                project_id: projectId,
                source_system: sourceSystem,
                file_id: fileId,
                table_name: selected.table_name,
                column_name: selected.column_name,
                status,
                definition: status === 'rejected' ? undefined : draftDescription,
            });
            await fetchReviewData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to save review decision.');
            console.error('Error saving decision:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleResetFilters = () => {
        setStatusFilter('all');
        setTableFilter('all');
        setColumnFilter('all');
        setPage(0);
    };

    const tableOptions = useMemo(() => reviewData?.tables || [], [reviewData]);
    const columnOptions = useMemo(() => reviewData?.columns_list || [], [reviewData]);

    if (loading && !reviewData) {
        return (
            <Box>
                <LinearProgress sx={{ mb: 2 }} />
                <Card>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={20} />
                        <Typography>Loading generated descriptions...</Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link to="/schema-analyzer/projects" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <Typography color="text.secondary" sx={{ '&:hover': { color: 'primary.main' } }}>
                        Projects
                    </Typography>
                </Link>
                <Link to={`/schema-analyzer/projects/${projectId}/upload`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <Typography color="text.secondary" sx={{ '&:hover': { color: 'primary.main' } }}>
                        {project?.name || 'Project'}
                    </Typography>
                </Link>
                <Typography color="text.primary">Description Review</Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(`/schema-analyzer/projects/${projectId}/upload`)}
                        variant="outlined"
                    >
                        Back
                    </Button>
                    <Box>
                        <Typography variant="h4" fontWeight={700}>
                            Generated Description Review
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {reviewData?.file_name || 'File'} | {sourceSystem}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {loading && reviewData && <LinearProgress sx={{ mb: 2 }} />}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} lg={7}>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Filter & Search
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        select
                                        label="Status"
                                        value={statusFilter}
                                        onChange={(event) => setStatusFilter(event.target.value)}
                                        fullWidth
                                    >
                                        {STATUS_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Autocomplete
                                        freeSolo
                                        options={tableOptions}
                                        value={tableFilter === 'all' ? null : tableFilter}
                                        inputValue={tableFilter === 'all' ? '' : tableFilter}
                                        onInputChange={(_, newInputValue) => {
                                            setTableFilter(newInputValue || 'all');
                                        }}
                                        onChange={(_, newValue) => {
                                            setTableFilter(newValue || 'all');
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Table"
                                                placeholder="Search tables"
                                            />
                                        )}
                                        clearOnEscape
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Autocomplete
                                        freeSolo
                                        options={columnOptions}
                                        value={columnFilter === 'all' ? null : columnFilter}
                                        inputValue={columnFilter === 'all' ? '' : columnFilter}
                                        onInputChange={(_, newInputValue) => {
                                            setColumnFilter(newInputValue || 'all');
                                        }}
                                        onChange={(_, newValue) => {
                                            setColumnFilter(newValue || 'all');
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Column"
                                                placeholder="Search columns"
                                            />
                                        )}
                                        clearOnEscape
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <Button variant="outlined" onClick={handleResetFilters}>
                                    Reset
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent sx={{ pb: 0 }}>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Generated Columns ({reviewData?.pagination?.total_items || 0})
                            </Typography>
                        </CardContent>
                        <TableContainer sx={{ maxHeight: 520 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Table</TableCell>
                                        <TableCell>Column</TableCell>
                                        <TableCell>Attribute</TableCell>
                                        <TableCell>Description</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(reviewData?.columns || []).map((row) => (
                                        <TableRow
                                            key={row.column_key}
                                            hover
                                            selected={selected?.column_key === row.column_key}
                                            onClick={() => setSelected(row)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell>{row.table_name}</TableCell>
                                            <TableCell>{row.column_name}</TableCell>
                                            <TableCell>{row.attribute_name || '-'}</TableCell>
                                            <TableCell sx={{ maxWidth: 260 }}>
                                                <Typography variant="body2" noWrap title={row.definition || ''}>
                                                    {row.definition || 'No description'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {!loading && (reviewData?.columns || []).length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <Typography color="text.secondary">
                                                    No generated descriptions found for the current filters.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                                Page {reviewData?.pagination?.page || 1} of {reviewData?.pagination?.total_pages || 1}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    select
                                    size="small"
                                    label="Rows"
                                    value={pageSize}
                                    onChange={(event) => {
                                        setPageSize(Number(event.target.value));
                                        setPage(0);
                                    }}
                                >
                                    {[10, 20, 30, 50].map((size) => (
                                        <MenuItem key={size} value={size}>
                                            {size}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={(reviewData?.pagination?.page || 1) <= 1}
                                    onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                                >
                                    Prev
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={(reviewData?.pagination?.page || 1) >= (reviewData?.pagination?.total_pages || 1)}
                                    onClick={() => setPage((prev) => prev + 1)}
                                >
                                    Next
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} lg={5}>
                    <Card sx={{ position: 'sticky', top: 16 }}>
                        <CardContent>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Review Panel
                            </Typography>
                            {!selected && (
                                <Typography color="text.secondary">
                                    Select a column on the left to review its generated description.
                                </Typography>
                            )}
                            {selected && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Chip label={selected.table_name} size="small" variant="outlined" />
                                        <Chip label={selected.column_name} size="small" color="primary" variant="outlined" />
                                        {selected.data_type && (
                                            <Chip label={selected.data_type} size="small" variant="outlined" />
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Attribute: {selected.attribute_name || 'N/A'}
                                    </Typography>

                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Generated Description
                                        </Typography>
                                        <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
                                            <CardContent sx={{ py: 1.5 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {selected.definition_generated_text || 'No generated description found.'}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Box>

                                    <Divider sx={{ my: 2 }} />

                                    <Typography variant="subtitle2" gutterBottom>
                                        Final Description
                                    </Typography>
                                    <TextField
                                        multiline
                                        minRows={5}
                                        value={draftDescription}
                                        onChange={(event) => setDraftDescription(event.target.value)}
                                        fullWidth
                                        placeholder="Edit the description before saving..."
                                    />

                                    <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<EditIcon />}
                                            disabled={!canSaveEdits}
                                            onClick={() => handleDecision('edited')}
                                        >
                                            Save Edits
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="warning"
                                            startIcon={<UndoIcon />}
                                            disabled={!selected || saving}
                                            onClick={() => handleDecision('pending')}
                                        >
                                            Reset
                                        </Button>
                                        {saving && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={16} />
                                                <Typography variant="caption">Saving...</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    {isDirty && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                            Edits detected. Use "Save Edits" to apply changes.
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}

export default DescriptionReviewPage;
